import { Table, Pagination, IconButton, Message, toaster, Modal, Button } from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import TrashIcon from '@rsuite/icons/Trash';
import CheckIcon from '@rsuite/icons/Check';
import CloseIcon from '@rsuite/icons/Close';
import { useState, useMemo, useEffect } from 'react';
import CheckRoundIcon from '@rsuite/icons/CheckRound';
import WarningRoundIcon from '@rsuite/icons/WarningRound';
import { createAccuracyARV, deleteAccuracyARV, updateAccuracyARV } from '../../util/db';
const { Column, HeaderCell, Cell } = Table;
import { SelectPicker } from 'rsuite';
import { calculateARVRow } from './ARVCalculation';
import SortUpIcon from '@rsuite/icons/SortUp';
import SortDownIcon from '@rsuite/icons/SortDown';
import SortIcon from '@rsuite/icons/Sort';

export default function ARVDataTable({
  rows,
  setARVData,
  locations,
  methods,
  instruments,
  parameters,
  tableLoading,
  setTableLoading,
  refreshData,
  materialOptions
}) {
  const [page, setPage] = useState(1);
  const [displayLength, setDisplayLength] = useState(10);
  const [sortColumn, setSortColumn] = useState('location');
  const [sortType, setSortType] = useState('asc');

  const [editingKey, setEditingKey] = useState(null);
  const [editData, setEditData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  function SortableHeaderCell({ columnKey, label, sortColumn, sortType, onSort }) {
    const handleClick = () => {
      let nextType = 'asc';
      if (sortColumn === columnKey && sortType === 'asc') nextType = 'desc';
      else if (sortColumn === columnKey && sortType === 'desc') nextType = null;
      onSort(columnKey, nextType);
    };

    let icon = <SortIcon style={{ fontSize: 14, color: '#ccc' }} />;
    if (sortColumn === columnKey) {
      if (sortType === 'asc') icon = <SortUpIcon style={{ fontSize: 14, color: 'green' }} />;
      if (sortType === 'desc') icon = <SortDownIcon style={{ fontSize: 14, color: 'red' }} />;
    }

    return (
      <div
        onClick={handleClick}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          fontWeight: 500
        }}
      >
        {label} {icon}
      </div>
    );
  }

  const toFixed3 = (val) =>
    typeof val === 'number' && !isNaN(val) ? parseFloat(val.toFixed(3)) : val;
  const mappedRows = useMemo(
    () =>
      rows.map((row, i) => ({
        key: row.key || row.id || `tmp-${i}`,
        id: row.id || null,
        isNew: row.isNew || false,
        location: row.location,
        round: row.round,
        instrument: row.instrument,
        method: row.method,
        parameter: row.parameter,
        labs: row.labs,
        material: row.material,
        size: row.size,
        arv: row.arv,
        measuredValue: row.measuredValue,
        delta: toFixed3(row.delta),
        reproducibility: toFixed3(row.reproducibility),
        deltaAllowable: toFixed3(row.deltaAllowable),
        passFail: row.passFail === true ? 'Pass' : row.passFail === false ? 'Fail' : '',
        notes: (row.notes ?? '').trim()
      })),
    [rows]
  );
  const sortedData = useMemo(() => {
    if (!sortColumn || !sortType) return mappedRows;
    return [...mappedRows].sort((a, b) => {
      let x = a[sortColumn];
      let y = b[sortColumn];

      if (x == null) return 1;
      if (y == null) return -1;

      if (typeof x === 'string') x = x.toLowerCase();
      if (typeof y === 'string') y = y.toLowerCase();

      if (sortType === 'asc') return x > y ? 1 : x < y ? -1 : 0;
      return x < y ? 1 : x > y ? -1 : 0;
    });
  }, [mappedRows, sortColumn, sortType]);

  const start = displayLength * (page - 1);
  const end = start + displayLength;
  const displayRows = sortedData.slice(start, end);

  const isEditing = (rowData) => editingKey === rowData.key;

  const handleCancel = (rowData) => {
    if (rowData.isNew) {
      setARVData((prev) => prev.filter((r) => r.key !== rowData.key));
    }
    setEditingKey(null);
    setEditData({});
  };

  const handleEdit = async (rowData) => {
    setTableLoading(true);
    try {
      const updatedRow = { ...rowData, ...editData };
      const calculatedRow = calculateARVRow(updatedRow, materialOptions);

      if (!calculatedRow.valid) {
        showToast('error', 'Unsupported parameter/method/material/size combination');
        return;
      }

      const payload = { ...calculatedRow, notes: updatedRow.notes ?? '' };

      if (rowData.isNew || !rowData.id) {
        // CREATE
        const { id, key, isNew, ...cleanPayload } = payload;
        const docRef = await createAccuracyARV(cleanPayload);
        showToast('success', 'Record created successfully!');

        setARVData((prev) =>
          prev.map((r) =>
            r.key === rowData.key ? { ...r, id: docRef.id, key: docRef.id, isNew: false } : r
          )
        );
      } else {
        // UPDATE
        const { id, key, isNew, ...cleanPayload } = payload;
        await updateAccuracyARV(rowData.id, cleanPayload);
        showToast('success', 'Record updated successfully!');
      }

      await refreshData();

      setARVData((prev) => prev.map((r) => (r.key === rowData.key ? { ...r, isNew: false } : r)));
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to save record');
    } finally {
      setEditingKey(null);
      setEditData({});
      setTableLoading(false);
    }
  };

  const renderEditableCell = (rowData, key) => {
    const nonEditable = ['delta', 'reproducibility', 'deltaAllowable', 'passFail', 'tool'];

    if (!isEditing(rowData) || nonEditable.includes(key)) {
      return typeof rowData[key] === 'object' ? (rowData[key]?.value ?? '') : (rowData[key] ?? '');
    }

    const inputStyle = {
      width: '100%',
      fontSize: '13px',
      height: '28px',
      alignItems: 'center'
    };

    if (['location', 'instrument', 'parameter'].includes(key)) {
      const options =
        key === 'location' ? locations : key === 'instrument' ? instruments : parameters;

      return (
        <SelectPicker
          data={options}
          value={editData[key] ?? rowData[key] ?? ''}
          onChange={(val) => {
            setEditData((prev) => ({ ...prev, [key]: val }));
          }}
          style={{ width: '100%' }}
          cleanable={false}
          size="sm"
        />
      );
    }

    if (key === 'method') {
      let options = methods;

      const param = editData.parameter ?? rowData.parameter;

      return (
        <SelectPicker
          data={options}
          value={editData.method ?? rowData.method ?? (param === 'Sulfur' ? 'D2622' : '')}
          onChange={(val) =>
            setEditData((prev) => ({
              ...prev,
              method: val,
              material: param === 'Sulfur' && val === 'D6667' ? 'Butane' : prev.material
            }))
          }
          style={{ width: '100%' }}
          cleanable={false}
          size="sm"
        />
      );
    }

    if (key === 'material') {
      const param = editData.parameter ?? rowData.parameter;
      const method = editData.method ?? rowData.method;

      let options = materialOptions;

      if (param === 'Sulfur' && method === 'D6667') {
        options = [{ label: 'Butane', value: 'Butane' }];
      }

      return (
        <SelectPicker
          data={options}
          value={
            editData.material ??
            rowData.material ??
            (param === 'Sulfur' && method === 'D6667' ? 'Butane' : '')
          }
          onChange={(val) => setEditData((prev) => ({ ...prev, material: val }))}
          style={inputStyle}
          cleanable={false}
          searchable={false}
          size="sm"
        />
      );
    }

    if (key === 'size') {
      const sizeOptions = [
        { label: '1L', value: '1L' },
        { label: '250 mL', value: '250 mL' }
      ];

      return (
        <SelectPicker
          data={sizeOptions}
          value={editData[key] ?? rowData[key] ?? '250 mL'}
          onChange={(val) => setEditData((prev) => ({ ...prev, [key]: val }))}
          style={inputStyle}
          cleanable={false}
          searchable={false}
          size="sm"
        />
      );
    }

    if (key === 'round') {
      return (
        <input
          type="text"
          defaultValue={editData[key] ?? ''}
          onChange={(e) => setEditData((prev) => ({ ...prev, [key]: e.target.value }))}
          style={{
            ...inputStyle,
            boxSizing: 'border-box',
            border: '1px solid #e5e5e5',
            borderRadius: '4px'
          }}
        />
      );
    }

    if (key === 'notes') {
      return (
        <input
          type="text"
          defaultValue={editData[key] ?? ''}
          onChange={(e) => setEditData((prev) => ({ ...prev, [key]: e.target.value }))}
          style={{
            ...inputStyle,
            boxSizing: 'border-box',
            border: '1px solid #e5e5e5',
            borderRadius: '4px'
          }}
        />
      );
    }

    if (['arv', 'measuredValue', 'labs'].includes(key)) {
      return (
        <input
          type="number"
          value={isEditing(rowData) ? (editData[key] ?? '') : (rowData[key] ?? '')}
          onChange={(e) => setEditData((prev) => ({ ...prev, [key]: e.target.value }))}
          style={{
            ...inputStyle,
            boxSizing: 'border-box',
            border: '1px solid #e5e5e5',
            borderRadius: '4px'
          }}
        />
      );
    }

    if (key === 'passFail') {
      return <input disabled value="" style={inputStyle} />;
    }

    return rowData[key] ?? '';
  };

  const showToast = (type, message) => {
    let icon;

    switch (type) {
      case 'success':
        icon = <CheckRoundIcon style={{ marginRight: 6 }} />;
        break;
      case 'error':
        icon = <WarningRoundIcon style={{ marginRight: 6 }} />;
        break;
      default:
        icon = null;
    }

    toaster.push(
      <Message type={type} closable showIcon icon={icon}>
        {message}
      </Message>,
      { placement: 'topEnd' }
    );
  };

  const isEditDataValid = (data) => {
    if (!data) return false;
    return (
      data.location?.trim() &&
      data.instrument?.trim() &&
      data.method?.trim() &&
      data.parameter?.trim() &&
      data.round?.trim() &&
      data.labs?.toString().trim() &&
      data.arv !== '' &&
      data.measuredValue !== ''
    );
  };

  const confirmDelete = async () => {
    if (!rowToDelete) return;
    if (rowToDelete.isNew || !rowToDelete.id) {
      console.log('rowToDelete', rowToDelete);
      setARVData((prev) => prev.filter((r) => r.key !== rowToDelete.key));
      showToast('success', 'Temporary row removed');
      setRowToDelete(null);
      setShowDeleteModal(false);
      return;
    }

    await handleDelete(rowToDelete);
    setRowToDelete(null);
    setShowDeleteModal(false);
  };

  const handleDelete = async (rowData) => {
    setTableLoading(true);
    try {
      if (rowData.isNew || !rowData.id) {
        setARVData((prev) => prev.filter((r) => r.key !== rowData.key));
        showToast('success', 'Temporary row removed');
      } else {
        await deleteAccuracyARV(rowData.id);
        showToast('success', 'Record deleted successfully');
        await refreshData();
      }
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to delete record');
    } finally {
      setTableLoading(false);
    }
  };

  const columns = [
    { key: 'location', label: 'Location', sortable: true },
    { key: 'round', label: 'Round', sortable: true },
    { key: 'instrument', label: 'Instrument', sortable: true },
    { key: 'method', label: 'Method', sortable: true },
    { key: 'parameter', label: 'Parameter', sortable: true },
    { key: 'labs', label: 'Labs' },
    { key: 'material', label: 'Material', sortable: true },
    { key: 'size', label: 'Size', sortable: true },
    { key: 'arv', label: 'ARV' },
    { key: 'measuredValue', label: 'Measured value' },
    { key: 'delta', label: 'Delta' },
    { key: 'reproducibility', label: 'R' },
    { key: 'deltaAllowable', label: 'Delta Allowable' },
    {
      key: 'passFail',
      label: 'P/F',
      render: (rowData) => (
        <span
          style={{
            color: rowData.passFail === 'Pass' ? 'green' : 'red'
          }}
        >
          {isEditing(rowData) ? '' : rowData.passFail}
        </span>
      )
    },
    {
      key: 'tool',
      label: 'Action',
      render: (rowData) =>
        isEditing(rowData) ? (
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
            <IconButton
              size="sm"
              icon={<CheckIcon />}
              appearance="primary"
              onClick={(e) => {
                e.currentTarget.blur();
                handleEdit(rowData);
              }}
              disabled={!isEditDataValid(editData)}
            />
            <IconButton
              size="sm"
              icon={<CloseIcon />}
              appearance="subtle"
              onClick={(e) => {
                e.currentTarget.blur();
                handleCancel(rowData);
              }}
            />
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <IconButton
              size="md"
              icon={<EditIcon style={{ fontSize: 18 }} />}
              onClick={(e) => {
                e.currentTarget.blur();
                setEditingKey(rowData.key);
                setEditData({ ...rowData, passFail: '' });
              }}
              appearance="subtle"
            />
            <IconButton
              size="md"
              icon={<TrashIcon style={{ fontSize: 18 }} />}
              onClick={(e) => {
                e.currentTarget.blur();
                if (rowData.isNew) {
                  setARVData((prev) => prev.filter((r) => r.key !== rowData.key));
                  setEditingKey(null);
                  setEditData({});
                  showToast('success', 'Temporary row removed');
                } else {
                  setRowToDelete(rowData);
                  setShowDeleteModal(true);
                }
              }}
              appearance="subtle"
              color="red"
            />
          </div>
        )
    },
    { key: 'notes', label: 'Notes' }
  ];

  return (
    <div>
      <Table
        autoHeight
        rowHeight={60}
        data={displayRows}
        loading={tableLoading}
        bordered
        cellBordered
        hover
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={(col, type) => {
          setSortColumn(col);
          setSortType(type);
        }}
        rowClassName={(rowData) => (rowData && rowData.isNew ? 'new-row' : '')}
        onRowClick={(rowData, e) => {
          if (e.detail === 2) {
            setEditingKey(rowData.key);
            setEditData({ ...rowData, passFail: '' });
          }
        }}
      >
        {columns.map(({ key, label, render }) => {
          let width;
          let align = 'center';

          if (['location', 'notes'].includes(key)) width = 300;
          if (['labs', 'arv', 'passFail', 'delta', 'reproducibility'].includes(key)) width = 80;
          if (['deltaAllowable', 'tool', 'measuredValue', 'instrument', 'material'].includes(key)) {
            width = 130;
          }
          if (['round', 'parameter', 'method', 'size'].includes(key)) {
            width = 100;
          }

          const isSortable = [
            'location',
            'round',
            'instrument',
            'method',
            'parameter',
            'material',
            'size'
          ].includes(key);

          return (
            <Column key={key} width={width} flexGrow={width ? 0 : 1} align={align}>
              <HeaderCell style={{ fontSize: '14px' }}>
                {isSortable ? (
                  <SortableHeaderCell
                    columnKey={key}
                    label={label}
                    sortColumn={sortColumn}
                    sortType={sortType}
                    onSort={(col, type) => {
                      setSortColumn(col);
                      setSortType(type);
                    }}
                  />
                ) : (
                  label
                )}
              </HeaderCell>
              <Cell
                dataKey={key}
                style={{
                  whiteSpace: 'normal',
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  ...(key === 'notes'
                    ? {
                        maxHeight: '60px',
                        overflowY: 'auto'
                      }
                    : {})
                }}
              >
                {(rowData) => (render ? render(rowData) : renderEditableCell(rowData, key))}
              </Cell>
            </Column>
          );
        })}
      </Table>

      <div style={{ padding: 5 }}>
        <Pagination
          prev
          next
          first
          last
          maxButtons={3}
          size="sm"
          layout={['-', 'total', '|', 'pager']}
          total={sortedData.length}
          limit={displayLength}
          activePage={page}
          onChangePage={setPage}
          onChangeLimit={(val) => {
            setDisplayLength(val);
            setPage(1);
          }}
        />
      </div>

      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this record?</Modal.Body>
        <Modal.Footer>
          <Button onClick={confirmDelete} appearance="primary" color="red">
            Delete
          </Button>
          <Button onClick={() => setShowDeleteModal(false)} appearance="subtle">
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
